import { TAU } from '../core/math';
import { RNG, rr } from '../core/rng';
import { STATE } from './fx';
import { P } from '../world/player';
import { diff, spawnEnemy } from './spawn';
import { densityMulAt } from '../data/pacing';
/* ============================================================
   編隊形狀 —— 「一個梯次怎麼擺進來」
   ============================================================

   M8 建立這一層的理由沒變：生成器均勻地在玩家周圍丟人，
   不論同屏 10 隻還是 110 隻，畫面讀起來都是同一種雜訊。
   密度變高只會變吵，不會變有趣。

   **M12 把這個檔削成純形狀**：一次擺放的幾何與行軍向量，沒有排程、
   沒有預告、沒有名字。「什麼時候放、放幾個梯次、怎麼講故事」
   搬到 `sim/waves.ts`（波次腳本層）＋ `data/waves.ts`（劇本）。

   拆開的理由很具體：鉗形攻勢的兩道牆**必須對向**，
   而舊的 `wall(t)` 自己抽方向、自己決定敵種 —— 呼叫端無從指定，
   所以組不出「同一波裡互相呼應的梯次」。
   現在每個形狀都吃一組選項（方向 `a`、數量 `n`、敵種 `kind`…），
   由劇本決定，形狀只負責擺。

   六種形狀（前四種對應 VS 查到的地圖事件，後兩種是我們的變化）：

     wall     橫掃牆   一列從單側入場，直線掃過全場，不理玩家
     ring     瞬時包圍 玩家周圍整圈同時出現，正常追擊
     closing  收縮環   一圈往內壓，不理玩家（VS 的 Flower Wall）
     rush     推進衝鋒 密集一團高速直線衝過去（VS 的 Minotaur Rush）
     arc      扇形佈陣 只佔一段弧、不行軍 —— 給遠程單位「架起來」用
     stream   持續溪流 單側的一條線，持續數秒（唯一有時間長度的形狀） */

/** 溪流是唯一「持續一段時間」的形狀，所以它有狀態。
    狀態放在這裡而不是模組層級的散裝變數：開新的一場要能整組歸零。 */
export var FS = {
  dir: 1, t: 0, cd: 0, a: 0, kind: 'nadel', gap: 0.28, march: 9
};

export function resetFormations() {
  FS.dir = 1; FS.t = 0; FS.cd = 0; FS.a = 0;
  FS.kind = 'nadel'; FS.gap = 0.28; FS.march = 9;
}

/** 這一波要放幾隻。跟著難度長，但有上限。

    **2026-08-02 上調**（使用者：「編隊密度還是太低，參考 VS 都是一大波的」）。
    舊值在第 10 分鐘只放大 2.5 倍（一道牆 22 隻），而 VS 的一波動輒
    三、四十隻 —— 一波要「看得出是一波」，數量本身就是演出。
    上限 3.6 倍是防呆：再高就變成一堵解不開的牆，那不是難度是死局。 */
export function size(base) {
  /* **編隊也要吃密度倍率**（`data/pacing.ts` 的 `DENSITY_MUL`）。
     2026-08-05 第一版只把倍率乘在 `targetField`（環境生成器）上，
     而波次是**另一條路徑** —— 一波三個梯次、每 46 秒一次，這個檔上面的
     註解自己寫著「在同屏才 25 隻的第 2 分鐘等於把場上的敵人翻倍」。
     結果：算出來第 4 分鐘該有 23 隻，實測 34 隻，五顆種子的存活
     只從 4.0 分動到 4.4 分。**「畫面上有多少東西」只能有一個事實來源。** */
  var k = densityMulAt(STATE.t);
  return Math.min(Math.round(base * 3.6 * k), Math.round(base * (1 + diff() * 0.8) * k));
}

function opt(o, k, d) { return o && o[k] !== undefined ? o[k] : d; }

/** 橫掃牆：一列從場邊入場，沿固定方向掃過全場。 */
export function wall(o?) {
  var a = opt(o, 'a', rr(0, TAU));
  var dirX = -Math.cos(a), dirZ = -Math.sin(a);
  var px = -dirZ, pz = dirX;                // 與行進方向垂直：牆的延展軸
  var n = opt(o, 'n', size(9)), span = opt(o, 'span', 34);
  var d = opt(o, 'dist', 40), kind = opt(o, 'kind', 'nadel'), mt = opt(o, 'march', 14);
  var cx = P.pos.x + Math.cos(a) * d, cz = P.pos.z + Math.sin(a) * d, got = 0;
  for (var i = 0; i < n; i++) {
    var f = (i / Math.max(1, n - 1) - 0.5) * span;
    var e = spawnEnemy(kind, cx + px * f, cz + pz * f);
    if (!e) continue;
    e.marchX = dirX; e.marchZ = dirZ; e.marchT = mt; got++;
  }
  return got;
}

/** 瞬時包圍：玩家周圍整圈同時出現，正常追擊。逼玩家立刻選一個方向突圍。 */
export function ring(o?) {
  var n = opt(o, 'n', size(12)), r = opt(o, 'dist', 26), kind = opt(o, 'kind', 'nadel');
  var off = opt(o, 'a', rr(0, TAU)), got = 0;
  for (var i = 0; i < n; i++) {
    var a = off + (i / n) * TAU;
    if (spawnEnemy(kind, P.pos.x + Math.cos(a) * r, P.pos.z + Math.sin(a) * r)) got++;
  }
  return got;
}

/** 收縮環：一圈往內壓。不理玩家，所以「往外突圍」是有效解 —— 這是它跟包圍的差別。 */
export function closing(o?) {
  var n = opt(o, 'n', size(14)), r = opt(o, 'dist', 34), kind = opt(o, 'kind', 'geissel');
  var off = opt(o, 'a', rr(0, TAU)), mt = opt(o, 'march', 11), got = 0;
  for (var i = 0; i < n; i++) {
    var a = off + (i / n) * TAU;
    var e = spawnEnemy(kind, P.pos.x + Math.cos(a) * r, P.pos.z + Math.sin(a) * r);
    if (!e) continue;
    /* 往環心走，不是往玩家走。玩家跑出環外之後這一波就過去了。 */
    e.marchX = -Math.cos(a); e.marchZ = -Math.sin(a); e.marchT = mt; got++;
  }
  return got;
}

/** 推進衝鋒：密集一團高速直線穿過。踩不到人就過去了，但正面接下必死。 */
export function rush(o?) {
  var a = opt(o, 'a', rr(0, TAU));
  var dirX = -Math.cos(a), dirZ = -Math.sin(a);
  var px = -dirZ, pz = dirX;
  var n = opt(o, 'n', size(11)), d = opt(o, 'dist', 38), kind = opt(o, 'kind', 'nadel');
  var cx = P.pos.x + Math.cos(a) * d, cz = P.pos.z + Math.sin(a) * d, got = 0;
  for (var i = 0; i < n; i++) {
    var e = spawnEnemy(kind, cx + px * rr(-7, 7), cz + pz * rr(-7, 7) + rr(-4, 4));
    if (!e) continue;
    e.marchX = dirX; e.marchZ = dirZ; e.marchT = opt(o, 'march', 12);
    e.st = 2;                               // 突刺者的衝刺狀態：直接進入高速
    got++;
  }
  return got;
}

/** 扇形佈陣：只佔一段弧、**不行軍**。遠程單位靠這個「架起來」，
    玩家看到的是「那一側被封住了」而不是「又有人追過來」。 */
export function arc(o?) {
  var a = opt(o, 'a', rr(0, TAU)), spread = opt(o, 'spread', 1.1);
  var n = opt(o, 'n', 4), r = opt(o, 'dist', 30), kind = opt(o, 'kind', 'lanze'), got = 0;
  for (var i = 0; i < n; i++) {
    var f = n === 1 ? 0 : (i / (n - 1) - 0.5) * spread;
    if (spawnEnemy(kind, P.pos.x + Math.cos(a + f) * r, P.pos.z + Math.sin(a + f) * r)) got++;
  }
  return got;
}

/** 持續溪流：一條線持續灌，一段時間。它不是一次性的，是一段壓力。 */
export function stream(o?) {
  FS.a = opt(o, 'a', RNG.next() < 0.5 ? 0 : Math.PI);
  FS.t = opt(o, 'dur', 6);
  FS.cd = 0;
  FS.kind = opt(o, 'kind', 'nadel');
  FS.gap = opt(o, 'gap', 0.28);
  FS.march = opt(o, 'march', 9);
  /* 第一隻立刻進場。等 0.28 秒才出第一隻的話，預告閃完之後有一段空窗，
     玩家會以為警告是假的 —— 而且其他形狀都是當下就成形，行為要一致。 */
  return pour();
}

function pour() {
  var cx = P.pos.x + Math.cos(FS.a) * 40, cz = P.pos.z + Math.sin(FS.a) * 40 + rr(-12, 12);
  var e = spawnEnemy(FS.kind, cx, cz);
  if (!e) return 0;
  e.marchX = -Math.cos(FS.a); e.marchZ = -Math.sin(FS.a); e.marchT = FS.march;
  return 1;
}

/** 溪流的持續灌注。放在 update 裡而不是一次生成完，才有「一直有東西進來」的壓力。 */
export function updateStream(dt) {
  if (FS.t <= 0) return;
  FS.t -= dt;
  FS.cd -= dt;
  if (FS.cd > 0) return;
  FS.cd = FS.gap;
  pour();
}

var KINDS = { wall: wall, ring: ring, closing: closing, rush: rush, arc: arc, stream: stream };

/** 指定形狀直接放一組。給測試與除錯主控台用 ——
    等一整個波次週期才看得到一次，驗證不了每一種都正常。 */
export function runFormation(id, o?) {
  var f = KINDS[id];
  return f ? f(o) : null;
}

/** 場上還在行軍的敵人數（診斷用：編隊有沒有真的形成）。 */
export function marchingCount(list) {
  var n = 0;
  for (var i = 0; i < list.length; i++) if (list[i].marchT > 0) n++;
  return n;
}

/** 目前的對局時鐘。劇本用它決定「這一波該用哪一種敵人」。 */
export function nowT() { return STATE.t; }
